import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from 'components/core/Modals/Modal/Modal';
import { Button, Grid, Icon, Input, Typhography } from 'components/core';
import { useOnboardManagedVendorMutation } from 'services/vendorsApi';
import { NotOnboardedVendor } from 'shared/models/services/vendors.model';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vendor: NotOnboardedVendor | null;
}

interface FormValues {
  business_name: string;
  payee_name: string;
  email: string;
  default_pay_mode: 'check' | 'digital_check';
  tax_id: string;
  allowPlaceholderEin: boolean;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  waiveFee: boolean;
}

const schema = yup.object({
  business_name: yup.string().trim().required('Business name is required'),
  payee_name: yup.string().trim().required('Check payee name is required'),
  email: yup.string().trim().email('Enter a valid email').required('Email is required'),
  default_pay_mode: yup
    .string()
    .oneOf(['check', 'digital_check'], 'Choose a check pay mode')
    .required(),
  allowPlaceholderEin: yup.boolean().default(false),
  tax_id: yup.string().when('allowPlaceholderEin', {
    is: true,
    then: (s) => s.default(''),
    otherwise: (s) =>
      s.test('ein', 'Enter a valid 9-digit EIN (or check “no verified EIN”)', (v) =>
        /^\d{9}$/.test((v ?? '').replace(/[-\s]/g, ''))
      ),
  }),
  street1: yup.string().trim().required('Street address is required'),
  street2: yup.string().trim().default(''),
  city: yup.string().trim().required('City is required'),
  state: yup.string().trim().required('State is required'),
  zip: yup.string().trim().required('ZIP is required'),
  waiveFee: yup.boolean().default(true),
});

const toFormValues = (v: NotOnboardedVendor | null): FormValues => ({
  business_name: v?.name ?? '',
  payee_name: v?.name ?? '',
  email: v?.email ?? '',
  default_pay_mode: 'digital_check',
  tax_id: v?.taxId ?? '',
  allowPlaceholderEin: false,
  street1: v?.street1 ?? '',
  street2: v?.street2 ?? '',
  city: v?.city ?? '',
  state: v?.state ?? '',
  zip: v?.zip ?? '',
  waiveFee: true,
});

const OnboardManagedModal = ({ isOpen, onClose, onSuccess, vendor }: Props) => {
  const [onboardManagedVendor, onboardState] = useOnboardManagedVendorMutation();

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore — yup.when/default shapes are looser than TS infers
    resolver: yupResolver(schema),
    defaultValues: toFormValues(vendor),
  });

  useEffect(() => {
    if (isOpen) reset(toFormValues(vendor));
  }, [isOpen, vendor, reset]);

  const allowPlaceholder = watch('allowPlaceholderEin');
  const isSaving = onboardState.isLoading;
  const serverError = (onboardState.error as any)?.data?.message;

  const onSubmit = async (values: FormValues) => {
    if (!vendor) return;
    const account = {
      company: {
        name: values.business_name.trim(),
        tax_id: (values.tax_id ?? '').replace(/[-\s]/g, ''),
      },
      email: values.email.trim().toLowerCase(),
      default_pay_mode: values.default_pay_mode,
      business_type: 'company' as const,
      language: 'english',
      allowPlaceholderEin: values.allowPlaceholderEin,
      check_recipient_details: {
        name: values.payee_name.trim(),
        address: values.street1.trim(),
        line2: values.street2.trim(),
        city: values.city.trim(),
        state: values.state.trim(),
        zip: values.zip.trim(),
      },
    };
    const link = {
      coupaSupplierId: vendor.coupaSupplierId,
      force: true,
      ...(values.waiveFee ? { checkProcessingFeeAmountOverride: 0 } : {}),
    };
    try {
      await onboardManagedVendor({ account, link }).unwrap();
      onSuccess();
    } catch {
      // serverError surfaces from mutation state
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalAdditionalClasses="!w-[560px] max-h-[86vh] overflow-y-auto items-center justify-center px-6 py-8 flex-col"
    >
      <Typhography
        component="p"
        type="h2"
        color="text-grey-900 mb-1 uppercase"
        additionalClasses="text-center"
      >
        COMPLETE ONBOARDING
      </Typhography>
      <Typhography
        component="p"
        type="h6"
        color="text-grey-600 mb-6"
        additionalClasses="text-center"
      >
        {vendor?.name ?? 'Vendor'} · check payment
      </Typhography>

      <div className="absolute cursor-pointer top-7 right-4 text-grey-600" onClick={onClose}>
        <Icon iconColor="inherit" iconType="CloseIcon" />
      </div>

      {serverError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded w-full">
          {serverError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 w-full">
        <Grid columnCount={2} columnGap={2}>
          <Controller
            control={control}
            name="business_name"
            render={({ field: { value } }) => (
              <Input
                id="business_name"
                labelName="business_name"
                type="text"
                label="Business name"
                {...register('business_name')}
                error={errors?.business_name?.message}
                value={value}
              />
            )}
          />
          <Controller
            control={control}
            name="payee_name"
            render={({ field: { value } }) => (
              <Input
                id="payee_name"
                labelName="payee_name"
                type="text"
                label="Check payee name"
                {...register('payee_name')}
                error={errors?.payee_name?.message}
                value={value}
              />
            )}
          />
        </Grid>

        <Grid columnCount={2} columnGap={2}>
          <Controller
            control={control}
            name="email"
            render={({ field: { value } }) => (
              <Input
                id="email"
                labelName="email"
                type="text"
                label="Email"
                {...register('email')}
                error={errors?.email?.message}
                value={value}
              />
            )}
          />
          <div className="relative flex flex-col w-full form-input-wrapper">
            <select
              id="default_pay_mode"
              {...register('default_pay_mode')}
              className="h-[52px] px-4 pt-4 border border-grey-300 rounded-lg text-14s text-grey-900 bg-white focus:outline-none focus:border-blue-500"
            >
              <option value="digital_check">Digital check (mailed)</option>
              <option value="check">Physical check</option>
            </select>
            <label
              htmlFor="default_pay_mode"
              className="text-14s font-normal text-grey-600 top-1 left-4 absolute"
            >
              Pay mode
            </label>
          </div>
        </Grid>

        <Controller
          control={control}
          name="tax_id"
          render={({ field: { value } }) => (
            <Input
              id="tax_id"
              labelName="tax_id"
              type="text"
              label="EIN (9 digits)"
              {...register('tax_id')}
              error={errors?.tax_id?.message}
              value={value}
              disabled={allowPlaceholder}
            />
          )}
        />

        <div className="relative flex flex-col w-full form-input-wrapper">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="allowPlaceholderEin"
              {...register('allowPlaceholderEin')}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label
              htmlFor="allowPlaceholderEin"
              className="text-14s font-normal text-grey-600 cursor-pointer"
            >
              No verified EIN on file — generate a placeholder (super-admin)
            </label>
          </div>
          {allowPlaceholder && (
            <p className="text-12s text-orange-600 mt-1 ml-7">
              A tagged placeholder EIN will be used. Update it once a W-9 is received.
            </p>
          )}
        </div>

        <Controller
          control={control}
          name="street1"
          render={({ field: { value } }) => (
            <Input
              id="street1"
              labelName="street1"
              type="text"
              label="Street address"
              {...register('street1')}
              error={errors?.street1?.message}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          name="street2"
          render={({ field: { value } }) => (
            <Input
              id="street2"
              labelName="street2"
              type="text"
              label="Suite / unit (optional)"
              {...register('street2')}
              error={errors?.street2?.message}
              value={value}
            />
          )}
        />

        <Grid columnCount={3} columnGap={2}>
          <Controller
            control={control}
            name="city"
            render={({ field: { value } }) => (
              <Input
                id="city"
                labelName="city"
                type="text"
                label="City"
                {...register('city')}
                error={errors?.city?.message}
                value={value}
              />
            )}
          />
          <Controller
            control={control}
            name="state"
            render={({ field: { value } }) => (
              <Input
                id="state"
                labelName="state"
                type="text"
                label="State"
                {...register('state')}
                error={errors?.state?.message}
                value={value}
              />
            )}
          />
          <Controller
            control={control}
            name="zip"
            render={({ field: { value } }) => (
              <Input
                id="zip"
                labelName="zip"
                type="text"
                label="ZIP"
                {...register('zip')}
                error={errors?.zip?.message}
                value={value}
              />
            )}
          />
        </Grid>

        <div className="relative flex flex-col w-full form-input-wrapper">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="waiveFee"
              {...register('waiveFee')}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="waiveFee" className="text-14s font-normal text-grey-600 cursor-pointer">
              Waive the check processing fee for this vendor
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
          <Button
            type="submit"
            additionalClasses="px-4 py-2"
            variants="default"
            isDisabled={isSaving}
            buttonLabel={isSaving ? 'Onboarding…' : 'Onboard vendor'}
          />
          <Button
            type="button"
            onClick={onClose}
            additionalClasses="px-4 py-2"
            variants="defaultOutlined"
            buttonLabel="Cancel"
          />
        </div>
      </form>
    </Modal>
  );
};

export default OnboardManagedModal;
